/**
 * @author    Nikko Renolds
 * @package		Trap Tycoon
 * @link		  https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments2026.
 * @license		MIT
 * @description Utility function to call backend API endpoints with proper headers and error handling.
 */

import { log } from "./logger";

// Automatically detect the API URL based on the current browser address
const API_URL = `${window.location.origin}/api`;

export const callBackend = async (endpoint, options = {}) => {
  // Get the token automatically so you don't have to pass it every time
  const token = localStorage.getItem("token");

  // Set default headers
  const headers = {
    "Content-Type": "application/json",
    ...options.headers, // Allow overriding if necessary
  };

  // Attach Authorization header if token exists
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Configure the request
  const config = {
    method: options.method || "GET",
    headers,
    ...options,
  };

  // Handle the Body (stringify automatically if it's an object)
  if (options.body && typeof options.body === "object") {
    config.body = JSON.stringify(options.body);
  }

  try {
    const response = await fetch(`${API_URL}${endpoint}`, config);

    // Check if the response is actually JSON before parsing
    const contentType = response.headers.get("content-type");
    let data;

    if (contentType && contentType.includes("application/json")) {
      data = await response.json();
    } else {
      // Server returned HTML or plain text (common during 404/500/Nginx errors)
      const errorText = await response.text();
      // Truncate long HTML responses for cleaner logs
      const cleanError =
        errorText.length > 100
          ? `${errorText.substring(0, 100)}...`
          : errorText;
      data = {
        error: cleanError || `Street Comms Protocol Error (${response.status})`,
      };
    }

    // specific error handling
    if (!response.ok) {
      // data.error now contains either the JSON error or the captured HTML/Text
      throw new Error(data.error || data.message || "Server Error");
    }

    return data;
  } catch (err) {
    if (endpoint !== "/system/log") {
      log("API", `API Call Failed (${endpoint}):`, err);
    } else {
      // Prevent the logging endpoint from infinitely looping on a crash
      console.error(
        `CRITICAL: Logging endpoint failed (${endpoint}):`,
        err.message,
      );
    }

    throw err;
  }
};
